// ApproverPicker.tsx
// Searchable dropdown for choosing a course approver (Admins / Super Admins).
// Supports filtering the list by name or email as the user types.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, ChevronDown, ShieldCheck } from 'lucide-react';
import type { AudienceUser } from '../../../../../services/PagesServices/SuperAdminMarketingService';
import './ApproverPicker.css';

interface ApproverPickerProps {
  users: AudienceUser[];
  value: string;
  onChange: (uuid: string) => void;
  placeholder?: string;
}

const roleLabel = (r: number) =>
  r === 4 ? 'Super Admin' : r === 3 ? 'Admin' : r === 2 ? 'Trainer' : 'User';

const ApproverPicker: React.FC<ApproverPickerProps> = ({
  users,
  value,
  onChange,
  placeholder = 'Select an approver',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = users.find((u) => u.uuid === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q)
    );
  }, [users, query]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (uuid: string) => {
    onChange(uuid);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="approver-picker" ref={rootRef}>
      <button
        type="button"
        className="approver-picker-control"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {selected ? (
          <span className="approver-picker-value">
            <span className="approver-picker-name">{selected.name?.trim() || selected.email}</span>
            <span className="approver-picker-email">{selected.email}</span>
          </span>
        ) : (
          <span className="approver-picker-placeholder">{placeholder}</span>
        )}
        <ChevronDown size={16} className="approver-picker-chevron" />
      </button>

      {open && (
        <div className="approver-picker-menu" role="listbox">
          <div className="approver-picker-search">
            <Search size={16} />
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email"
            />
          </div>
          <div className="approver-picker-list">
            {filtered.length === 0 && (
              <div className="approver-picker-empty">No matching users</div>
            )}
            {filtered.map((u) => (
              <button
                type="button"
                key={u.uuid}
                role="option"
                aria-selected={u.uuid === value}
                className={`approver-picker-item ${u.uuid === value ? 'selected' : ''}`}
                onClick={() => pick(u.uuid)}
              >
                <span className="approver-picker-item-main">
                  <span className="approver-picker-name">{u.name?.trim() || u.email}</span>
                  <span className="approver-picker-email">{u.email}</span>
                </span>
                <span className="approver-picker-role">
                  <ShieldCheck size={12} /> {roleLabel(u.roleId)}
                </span>
                {u.uuid === value && <Check size={16} className="approver-picker-check" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproverPicker;
